/**
 * PATH: packages/ui/src/kanban/useIsPhone.ts
 * FILE_ID: UI-KANBAN-USE-IS-PHONE-001
 * PURPOSE: Reactive "are we on a phone-width viewport?" hook for kanban
 *   overlays that switch between an anchored popover and a bottom sheet.
 * WHY:
 *   The card thread peek (and, by precedent, KanbanContextMenu) needs the JS
 *   view and the CSS `md:` view to agree on the phone breakpoint. Using
 *   `matchMedia('(max-width: 767px)')` — the same query Tailwind's `md:`
 *   resolves against — instead of polling `innerWidth` keeps them in lockstep
 *   and updates live on rotate/resize. SSR-safe (returns false when there's no
 *   `window`/`matchMedia`).
 * FLOW:
 *   ┌──────────────┐   ┌────────────────────┐   ┌──────────────┐
 *   │ mount        │ → │ matchMedia(767px)  │ → │ boolean +    │
 *   │              │   │ subscribe(change)  │   │ live updates │
 *   └──────────────┘   └────────────────────┘   └──────────────┘
 * RELATED:
 *   - components/KanbanCardPeek.tsx / KanbanCardPeekHandle.tsx (consumers).
 *   - components/KanbanContextMenu.tsx (same breakpoint, inline copy).
 */
import { useEffect, useState } from 'react'

const PHONE_QUERY = '(max-width: 767px)'

/** `true` when the viewport is phone-width (Tailwind `< md`). Live-updates on
 *  resize/rotate; safe to call during SSR (returns `false`). */
export function useIsPhone(): boolean {
  const [isPhone, setIsPhone] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia(PHONE_QUERY).matches
  })

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia(PHONE_QUERY)
    const onChange = (e: MediaQueryListEvent) => setIsPhone(e.matches)
    // Older Safari exposes addListener/removeListener instead of the
    // EventTarget API.
    if (mq.addEventListener) mq.addEventListener('change', onChange)
    else mq.addListener(onChange)
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange)
      else mq.removeListener(onChange)
    }
  }, [])

  return isPhone
}
